import {
  Badge,
  Card,
  SkeletonLoader,
  Tooltip,
  UserAvatar,
  type UserAvatarProps as TUserAvatarProps,
} from '@payaca/ambrosia';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React, { ComponentPropsWithRef, FC } from 'react';
import { Link } from 'react-router-dom';
import { AddressShape } from '../address/types';
import Tag from '../tag/Tag';
import Address1Line from './Address1Line';

export type TProps = {
  reference: string;
  onCopyReference?: () => void;
  customerName?: string;
  address?: AddressShape;
  assignee?: TUserAvatarProps['user'];
  summaryInfo?: {
    label: string;
    value: string;
    iconName: TIconName;
  }[];
  tags?: { colour: string; label: string }[];
  alerts?: {
    helperText?: string;
    iconName: TIconName;
    colour: 'black' | 'gray' | 'green' | 'blue' | 'red' | 'yellow';
  }[];
};

const ProjectCardPolymorphic: FC<
  TProps & { as: 'div' | typeof Link } & React.ComponentPropsWithRef<
      'div' | typeof Link
    >
> = (props) => {
  const {
    ref,
    as,
    reference,
    onCopyReference,
    customerName,
    address,
    assignee,
    summaryInfo,
    tags,
    alerts,
    className,
    ...rest
  } = props;

  return (
    <Card
      as={as}
      ref={ref}
      className={clstx(
        'border-blue-600 border-2 shadow-none shadow-project-card transition',
        'hover:-translate-x-1 hover:-translate-y-1 hover:shadow-project-card-hover',
        'active:translate-x-0 active:translate-y-0 active:shadow-none active:no-underline',
        className
      )}
      sizeVariant="sm"
      {...rest}
    >
      <Card.Body>
        <div className="mb-3">
          <div className="flex justify-between">
            <div>
              <p
                className="supporting-body text-gray-500 group"
                onClick={
                  onCopyReference
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onCopyReference();
                      }
                    : undefined
                }
              >
                #{reference}{' '}
                {onCopyReference && (
                  <UntitledIcon
                    className="h-4 w-4 hidden group-hover:inline-block"
                    name="copy-07.3"
                  />
                )}
              </p>
              {customerName && <p>{customerName}</p>}
            </div>

            {!!assignee && (
              <UserAvatar
                user={assignee}
                sizeVariant="xs"
                enableUserInfoTooltip
              />
            )}
          </div>
          {!!address && (
            <Address1Line
              className="supporting-body text-gray-500"
              address={address}
            />
          )}
        </div>

        <div className="empty:hidden relative flex flex-wrap items-center gap-1.5">
          {alerts?.map(({ helperText, iconName, colour }, i) => (
            <Tooltip
              key={`alert-${i}`}
              className="flex"
              enabled={!!helperText}
              tooltipContent={helperText}
            >
              <UntitledIcon
                className={clstx(
                  'h-5 w-5',
                  colour === 'black' && 'text-black',
                  colour === 'gray' && 'text-gray-700',
                  colour === 'green' && 'text-green-700',
                  colour === 'blue' && 'text-blue-700',
                  colour === 'red' && 'text-red-700',
                  colour === 'yellow' && 'text-yellow-700'
                )}
                name={iconName}
              />
            </Tooltip>
          ))}

          {summaryInfo?.map(({ label, value, iconName }) => (
            <Tooltip key={label} tooltipContent={label}>
              <Badge variant="soft" colour="gray" size="sm">
                <UntitledIcon
                  name={iconName}
                  className="h-3 w-3 shrink-0"
                  aria-hidden
                />
                <span className="sr-only">{label}:</span>
                <span className="min-w-0 truncate">{value}</span>
              </Badge>
            </Tooltip>
          ))}

          {tags?.map(({ colour, label }) => (
            <Tag key={label} colour={colour}>
              {label}
            </Tag>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

const ProjectCardSkeletonLoader: FC<ComponentPropsWithRef<'div'>> = (props) => {
  const { ref, className, ...rest } = props;

  return (
    <Card
      ref={ref}
      className={clstx(
        'border-blue-600 border-2 shadow-none shadow-project-card transition',
        className
      )}
      sizeVariant="sm"
      {...rest}
    >
      <Card.Body className="space-y-4">
        <div className="flex justify-between gap-4 items-center">
          <SkeletonLoader.Title />

          <SkeletonLoader.Circle className="shrink-0" />
        </div>
        <SkeletonLoader.Text />
        <SkeletonLoader.Text />
        <SkeletonLoader.Text />
      </Card.Body>
    </Card>
  );
};

export const ProjectCardLink: FC<
  TProps & React.ComponentPropsWithRef<typeof Link>
> = (props) => {
  const { className, ...rest } = props;

  return (
    <ProjectCardPolymorphic
      as={Link}
      className={clstx(
        'text-[var(--tw-prose-root)] font-normal hover:no-underline',
        className
      )}
      {...rest}
    />
  );
};

const ProjectCard: FC<TProps & React.ComponentPropsWithRef<'div'>> = (
  props
) => {
  return <ProjectCardPolymorphic as="div" {...props} />;
};

export default Object.assign(ProjectCard, {
  SkeletonLoader: ProjectCardSkeletonLoader,
});
